// src/utils/contentProtection.js
//
// ── WHAT THIS CAN AND CANNOT DO — please read before extending this file ──
//
// No web technology can PREVENT a determined person from reading or copying
// text that a browser renders on screen — the browser must have the plain
// content to display it, so it's always available to DevTools, browser
// extensions, screen readers, OS-level screenshot/recording tools, or a
// second camera pointed at the monitor. Any product that claims to make
// on-screen text "impossible to copy or screenshot" is describing a UX
// deterrent, not a security boundary. Please don't represent this module as
// doing more than that internally or to users — it would be an inaccurate
// (and legally risky) claim about what the reader actually guarantees.
//
// What DOES work, and what this module implements, mirrors the layered
// approach already used for the offline USB product (see the encryption +
// watermarking + query-throttling notes from that project): raise the cost
// of casual copying, and make anything that DOES leak traceable back to an
// account.
//
//   1. Blank the content when the tab/window isn't focused (blur / hidden).
//      Defeats casual screenshots taken while alt-tabbed, most "capture the
//      active window" tools, and shoulder-surfing during a screen share.
//      Does NOT defeat full-desktop screen recording — nothing client-side
//      can, since that captures the whole display regardless of focus.
//
//   2. A best-effort DevTools-open heuristic (window-size delta) that blanks
//      content while DevTools looks open. This is a heuristic, not a
//      detection guarantee — undocked/remote DevTools, disabling JS before
//      opening, and browser automation (Puppeteer/Playwright) can all sit
//      outside what this catches. Treat it as raising the bar, not a wall.
//
//   3. Block printing (CSS + JS), since Print > Save as PDF is one of the
//      more common casual bulk-export paths for on-screen HTML.
//
//   4. A visible, per-user watermark baked into the content background
//      (see buildWatermarkBackground in ReaderPage.js) so that if content
//      IS captured despite the above, it's attributable to a specific
//      account rather than anonymous.
//
// None of this replaces the actual security work already covering this
// content: AES-encrypted transport + at-rest storage, auth-gated chunked
// delivery, and copy/right-click/save-shortcut blocking at the app level
// (see App.js). This module is the last, weakest layer, not the important
// one — server-side rate limiting on chapter/chunk fetches (throttling how
// fast one account can pull an entire book) matters far more for stopping
// bulk scraping than anything client-side ever can, and isn't something this
// file can do — that needs to happen in the API.

import { useEffect, useRef, useState } from 'react';

// Chrome/Firefox/Edge, when DevTools is docked to a side of the window,
// reliably shrinks window.innerWidth or innerHeight relative to outerWidth/
// outerHeight by more than any normal browser chrome (toolbars, scrollbars)
// would. This threshold is intentionally generous to avoid false positives
// on legitimately narrow browser chrome / high-DPI edge cases.
const DEVTOOLS_DELTA_THRESHOLD = 160;

function looksLikeDevToolsOpen() {
  if (typeof window === 'undefined') return false;
  const widthDelta = window.outerWidth - window.innerWidth;
  const heightDelta = window.outerHeight - window.innerHeight;
  return widthDelta > DEVTOOLS_DELTA_THRESHOLD || heightDelta > DEVTOOLS_DELTA_THRESHOLD;
}

export function useContentProtection({ userLabel } = {}) {
  const [isWiped, setIsWiped] = useState(false);
  const [wipeReason, setWipeReason] = useState(null);
  const printingRef = useRef(false);

  useEffect(() => {
    // Single source of truth, re-evaluated from scratch on every relevant
    // event/tick, rather than separate handlers independently calling
    // wipe()/unwipe() — avoids handlers racing/stomping on each other's
    // state (e.g. a blur-triggered wipe getting silently cleared a moment
    // later by an unrelated focus handler while DevTools is still open).
    const evaluate = () => {
      let reason = null;
      if (printingRef.current) reason = 'print';
      else if (typeof document !== 'undefined' && document.hidden) reason = 'inactive';
      else if (looksLikeDevToolsOpen()) reason = 'devtools';
      setWipeReason(reason);
      setIsWiped(!!reason);
    };

    const handleBeforePrint = () => { printingRef.current = true; evaluate(); };
    const handleAfterPrint = () => { printingRef.current = false; evaluate(); };

    document.addEventListener('visibilitychange', evaluate);
    window.addEventListener('blur', evaluate);
    window.addEventListener('focus', evaluate);
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
    // Polled rather than event-driven because there's no browser event for
    // "DevTools opened" — this is the standard workaround industry-wide.
    // 800ms keeps CPU cost negligible while still catching it quickly enough
    // to matter.
    const pollId = setInterval(evaluate, 800);

    evaluate();

    return () => {
      document.removeEventListener('visibilitychange', evaluate);
      window.removeEventListener('blur', evaluate);
      window.removeEventListener('focus', evaluate);
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
      clearInterval(pollId);
    };
  }, []);

  const watermarkText = `${userLabel || 'AIR Online'} \u00B7 ${new Date().toLocaleDateString()}`;

  return { isWiped, wipeReason, watermarkText };
}
